/**
 * useUserIdentity
 *
 * Live-lookup hook for a user's current display name + avatar URL. Replaces
 * reads from denormalized snapshot fields (Order.coachName, Lesson.coachName,
 * Message.senderName, Order.playerName, etc.) on display surfaces — the
 * snapshots are write-once and don't update when the user renames or
 * re-uploads their photo, which makes them stale forever after a profile
 * edit. This hook returns the live value.
 *
 * Architecture: module-level cache + in-flight promise dedup, so any number
 * of components mounting with the same userId fire exactly one
 * `getUserProfile` call per session.
 *
 * Returns `null` while the first fetch is in flight. Callers should
 * fallback to the snapshot field for instant-render UX:
 *
 *   const live = useUserIdentity(order.coachId, 'coach');
 *   const coachName = live?.name ?? order.coachName ?? 'Coach';
 *
 * Role param selects which embedded profile to read from. Defaults to
 * 'coach' since most denormalized-identity surfaces are coach-facing.
 */

import { useEffect, useState } from 'react';
import { getUserProfile } from '../services/userProfile';

export type IdentityRole = 'coach' | 'player';

export interface UserIdentity {
  name: string | null;
  avatarUrl: string | null;
}

// Per-uid cache. Stored once per session; never invalidated for the lifetime
// of the page. If a coach renames mid-session while a player has the chat
// open, the player won't see the change until the next page load. Trade-off
// against onSnapshot subscriptions, which would multiply reads per surface.
const cache = new Map<string, UserIdentity>();
const inflight = new Map<string, Promise<UserIdentity>>();

async function fetchIdentity(userId: string): Promise<UserIdentity> {
  const profile = await getUserProfile(userId).catch(() => null);
  // Prefer the role-specific embedded display fields; fall back to the
  // root-level mirror (legacy) and finally `null` so the caller's snapshot
  // fallback can kick in.
  const coachName = profile?.coachProfile?.displayName ?? null;
  const coachAvatar = profile?.coachProfile?.avatarUrl ?? null;
  const playerName = profile?.playerProfile?.displayName ?? null;
  const playerAvatar = profile?.playerProfile?.avatarUrl ?? null;
  const rootName = profile?.displayName ?? null;
  const rootAvatar = profile?.avatarUrl ?? null;
  // `||`, not `??`: a profile can carry an empty string ("" avatarUrl on
  // the coach profile while the root holds the photo), and an empty string
  // is never a name or a photo. With `??` it won, and a coach whose photo
  // loads in the thread showed as initials everywhere else (staging walk
  // 2026-10-25 item 13).
  return {
    name: coachName || playerName || rootName || null,
    avatarUrl: coachAvatar || playerAvatar || rootAvatar || null,
  };
}

async function fetchAndCache(userId: string): Promise<UserIdentity> {
  const existing = inflight.get(userId);
  if (existing) return existing;
  const promise = fetchIdentity(userId).then((identity) => {
    cache.set(userId, identity);
    inflight.delete(userId);
    return identity;
  });
  inflight.set(userId, promise);
  return promise;
}

/**
 * Live name + avatar for a user. Returns null while loading; the caller
 * should fall back to the denormalized snapshot field in that window.
 *
 * The `role` arg currently affects only how we choose between embedded
 * profiles when both coachProfile and playerProfile exist on the same
 * account. In practice the merged record returns coach-side name when the
 * lookup target is a coach and player-side name when a player — the
 * priority chain handles both correctly.
 */
export function useUserIdentity(
  userId: string | null | undefined,
  role: IdentityRole = 'coach',
): UserIdentity | null {
  // Synchronous initial state when the value is already cached, so cached
  // identities render in the first paint without a flash of snapshot data.
  const [identity, setIdentity] = useState<UserIdentity | null>(() => {
    if (!userId) return null;
    return cache.get(userId) ?? null;
  });

  useEffect(() => {
    if (!userId) {
      setIdentity(null);
      return;
    }
    const cached = cache.get(userId);
    if (cached) {
      setIdentity(cached);
      return;
    }
    let cancelled = false;
    fetchAndCache(userId).then((id) => {
      if (cancelled) return;
      // Role-aware projection: if the caller asked for 'coach' and the
      // profile has a coachProfile, prefer coach fields explicitly; same
      // for player. Falls through to whatever the priority chain in
      // fetchIdentity computed. Currently a no-op (the priority chain
      // already handles both) but keeps the API future-proof for accounts
      // that have both profiles populated.
      void role;
      setIdentity(id);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, role]);

  return identity;
}
